import React, { useEffect, useRef, useState } from 'react'
import { User } from 'oidc-client';
import { AgTechAuthContext, AuthContext, useAgTechAuthContext } from '@agtech/core/auth/AgTechAuthContext';
import AgTechWebAuthenticationService from './AgTechWebAuthenticationService';
import { AgTechApplication } from '@agtech/core/app/AgTechApplication';
import { userHasPermission } from '@agtech/core/auth/AgTechAuthData';

declare type AuthContextState = {
    user: User | null,
    hasLoaded: boolean
}

const DefaultUser = {
    id: '',
    name: 'Mock User',
    username: 'mockusername',
    email: 'mockuser@gmail.com',
    phoneNumber: '111-123-2932',
    token: '',
    role: ['user'],
    permissions: []
};

// The server issues a claim as a single string when there's exactly one value, or an array
// when there's more than one - normalize either shape to an array.
const toStringArray = (claim: string | string[] | undefined): string[] => {
    if (!claim) {
        return [];
    }

    return typeof claim === 'string' ? [claim] : claim;
};

const AgTechWebAppAuthContext = (props: {
    app: AgTechApplication,
    children: JSX.Element
}) => {
    const appAuthConfiguration = props.app.auth;
    const authService = useRef(new AgTechWebAuthenticationService(appAuthConfiguration));

    const [authState, updateAuthState] = useState<AuthContextState>({
        user: null,
        hasLoaded: false
    });

    const doesAppRequireAuthentication = appAuthConfiguration !== undefined && appAuthConfiguration.usesAuth;

    useEffect(() => {
        if (doesAppRequireAuthentication) {
            authService.current.getUser()
                .then(user => {                   
                    updateAuthState({
                        user: user,
                        hasLoaded: true
                    });
                });
        }
        else {
            updateAuthState({
                user: null,
                hasLoaded: true
            });
        }
    }, []);

    let authContext: AgTechAuthContext = {
        getLoggedInUser: async () => {
            if (doesAppRequireAuthentication) {
                let loggedInUser = await authService.current.getUser();

                return {
                    id: loggedInUser?.profile?.sub ?? '',
                    name: loggedInUser?.profile?.name ?? '',
                    username: loggedInUser?.profile?.preferred_username ?? '',
                    token: loggedInUser?.access_token ?? '',
                    role: toStringArray(loggedInUser?.profile?.role as string | string[] | undefined),
                    permissions: toStringArray(loggedInUser?.profile?.permission as string | string[] | undefined)
                }
            }
            else {
                return DefaultUser;
            }
        },
        isUserAuthenticated: () => {
            return !doesAppRequireAuthentication || (authState.user?.access_token !== undefined);
        },
        loginUser: () => authService.current.login(),
        onSuccessfulUserLogin: async props => {
            await authService.current.signinRedirectCallback(props)
                .then(loggedInUser => {                    
                    updateAuthState({
                        user: loggedInUser,
                        hasLoaded: true
                    });

                    props.navigateToRoot();
                })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        },
        logoutUser: () => authService.current.signoutRedirectCallback(),
        user: doesAppRequireAuthentication ? {
            id: authState.user?.profile?.sub ?? '',
            username: authState.user?.profile?.preferred_username ?? '',
            name: authState.user?.profile?.name ?? '',
            token: authState.user?.access_token ?? '',
            role: toStringArray(authState.user?.profile?.role as string | string[] | undefined),
            permissions: toStringArray(authState.user?.profile?.permission as string | string[] | undefined)
        } : DefaultUser,
        config: appAuthConfiguration
    };

    return authState.hasLoaded ? (
        <AuthContext.Provider value={authContext}>
            {props.children}
        </AuthContext.Provider>
    ) : null
};

export const useUserRoles = () => useAgTechAuthContext().user.role;
export const useUserPermissions = () => useAgTechAuthContext().user.permissions;
export const useHasPermission = (permission: string) => userHasPermission(useAgTechAuthContext().user, permission);

export default React.memo(AgTechWebAppAuthContext);